/**
 * AP-47 6.2 — **Mängelliste der Montageprotokolle** im Listen-Standard: FilterBar, Spalten über
 * `ui-settings`, Sortierung, Seiten, Export samt Mail. Mängel stornierter Protokolle und von Entwürfen
 * erscheinen nicht. Mit `sitedocs.assembly.manage` „als behoben vermerken" und zurücknehmen (6.3 Weg 2).
 *
 * Paginiert, gefiltert und sortiert auf dem Server. Muster: `SiteDocsListPage`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowDown, ArrowUp, ArrowUpDown, Check, Settings } from 'lucide-react'
import { Modal } from '@symphenia/ui-components'
import { MONTAGE_MANGEL_ZUSTAENDE, erfuelltRecht, type MontageMangelZeile } from '@symphenia/shared-types'
import { FilterBar, filterAusAdresse } from '@/components/list/FilterBar'
import type { ActiveFilter, FilterFieldDef } from '@/components/list/list.types'
import { HelpPopover } from '@/components/ui/HelpPopover'
import { ExportButton } from '@/components/ui/ExportButton'
import { LayoutDefaultActions } from '@/components/ui/LayoutDefaultActions'
import { useAuthStore } from '@/lib/auth.store'
import { fehlertext } from '@/lib/fehlertext'
import { fmtDate } from '@/lib/format-date'
import { pageWide } from '@/lib/form-styles'
import { feldKlasse } from '@/lib/speichern'
import { loadUiSetting, markUiSettingApplied, saveUiSetting } from '@/lib/ui-settings'
import { useTerminology } from '@/lib/useTerminology'
import { assemblyDefectsExportRequest, fetchAssemblyDefects, reopenAssemblyDefect, resolveAssemblyDefect, type DefectListe, type ListDefectsParams } from '../api/site-docs.api'

const LAYOUT_KEY = 'columnbar_layout_site_docs_defects'

interface ColDef {
  key: string
  label: string
  sortable?: boolean
}

interface ColumnState {
  columnKeys: string[]
  columnWidths: Record<string, number>
  sortKey: string
  sortDir: 'asc' | 'desc'
}

const DEFAULT_COLUMN_KEYS = ['projectLabel', 'documentNumber', 'nr', 'partName', 'text', 'weight', 'dueDate', 'state', 'task']
const DEFAULT_FILTER_FIELD_KEYS = ['q', 'state', 'weight', 'overdue']
const STANDARD: ColumnState = { columnKeys: DEFAULT_COLUMN_KEYS, columnWidths: {}, sortKey: 'documentDate', sortDir: 'desc' }
const SERVER_SORTIERBAR = new Set(['documentDate', 'dueDate', 'state', 'weight', 'nr', 'projectLabel'])

function filterParams(filters: Record<string, ActiveFilter>, documentId: string | null): ListDefectsParams {
  const p: ListDefectsParams = documentId ? { documentId } : {}
  for (const [key, a] of Object.entries(filters)) {
    if (!a.value) continue
    if (key === 'q') p.q = a.value
    if (key === 'state') p.state = a.value
    if (key === 'weight') p.weight = a.value as ListDefectsParams['weight']
    if (key === 'overdue' && a.value === 'true') p.overdue = true
    if (key === 'from') p.from = a.value.slice(0, 10)
    if (key === 'to') p.to = a.value.slice(0, 10)
  }
  return p
}

export default function MontageMaengelPage() {
  const { t } = useTranslation()
  const terms = useTerminology()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const darfVermerken = erfuelltRecht('sitedocs.assembly.manage', user?.permissions)
  const darfExport = erfuelltRecht('sitedocs.export.run', user?.permissions)
  const [suchParameter] = useSearchParams()
  const documentId = suchParameter.get('documentId')

  const filterFields: FilterFieldDef[] = useMemo(
    () => [
      { key: 'q', label: t('siteDocs.defects.filter.q'), type: 'text', category: 'core', operators: ['contains'] },
      { key: 'state', label: t('siteDocs.defects.filter.state'), type: 'select-multi', category: 'core', operators: ['is'], options: MONTAGE_MANGEL_ZUSTAENDE.map((s) => ({ value: s, label: t(`siteDocs.assembly.states.${s}`) })) },
      { key: 'weight', label: t('siteDocs.defects.filter.weight'), type: 'select', category: 'core', operators: ['is'], options: (['WESENTLICH', 'UNWESENTLICH'] as const).map((w) => ({ value: w, label: t(`siteDocs.assembly.weights.${w}`) })) },
      { key: 'overdue', label: t('siteDocs.defects.filter.overdue'), type: 'boolean', category: 'core', operators: ['is'] },
      { key: 'from', label: t('siteDocs.filter.from'), type: 'date', category: 'core', operators: ['is'] },
      { key: 'to', label: t('siteDocs.filter.to'), type: 'date', category: 'core', operators: ['is'] },
    ],
    [t],
  )
  const alleSpalten: ColDef[] = useMemo(
    () => [
      { key: 'projectLabel', label: terms.project, sortable: true },
      { key: 'documentNumber', label: t('siteDocs.defects.columns.document') },
      { key: 'documentDate', label: t('siteDocs.columns.date'), sortable: true },
      { key: 'nr', label: t('siteDocs.defects.columns.nr'), sortable: true },
      { key: 'partName', label: t('siteDocs.defects.columns.part') },
      { key: 'text', label: t('siteDocs.defects.columns.text') },
      { key: 'weight', label: t('siteDocs.defects.columns.weight'), sortable: true },
      { key: 'dueDate', label: t('siteDocs.defects.columns.due'), sortable: true },
      { key: 'state', label: t('siteDocs.defects.columns.state'), sortable: true },
      { key: 'resolvedNote', label: t('siteDocs.defects.columns.note') },
      { key: 'task', label: t('siteDocs.defects.columns.task') },
    ],
    [t, terms.project],
  )

  const [adresse] = useState(() => filterAusAdresse(suchParameter, filterFields))
  const ladeSpalten = (): ColumnState => {
    const g = loadUiSetting<Partial<ColumnState> | null>(LAYOUT_KEY, null)
    return g?.columnKeys?.length ? { columnKeys: g.columnKeys, columnWidths: g.columnWidths ?? {}, sortKey: g.sortKey ?? 'documentDate', sortDir: g.sortDir ?? 'desc' } : STANDARD
  }

  const [filterValues, setFilterValues] = useState<Record<string, ActiveFilter>>({})
  const [colState, setColState] = useState<ColumnState>(ladeSpalten)
  const [columnConfigMode, setColumnConfigMode] = useState(false)
  const [result, setResult] = useState<DefectListe | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [vermerk, setVermerk] = useState<{ zeile: MontageMangelZeile; art: 'resolve' | 'reopen' } | null>(null)
  const [notiz, setNotiz] = useState('')
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dragKeyRef = useRef<string | null>(null)
  const [dragOverKey, setDragOverKey] = useState<string | null>(null)

  const fetchData = useCallback(
    async (filters: Record<string, ActiveFilter>, seite: number, state: ColumnState) => {
      setLoading(true)
      setError(null)
      try {
        setResult(await fetchAssemblyDefects({ ...filterParams(filters, documentId), page: seite, limit: 50, sort: SERVER_SORTIERBAR.has(state.sortKey) ? state.sortKey : undefined, dir: state.sortDir }))
      } catch {
        setError(t('siteDocs.defects.loadError'))
      } finally {
        setLoading(false)
      }
    },
    [t, documentId],
  )

  useEffect(() => {
    void fetchData(filterValues, page, colState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  const handleFiltersChange = useCallback(
    (neu: Record<string, ActiveFilter>) => {
      setFilterValues(neu)
      setPage(1)
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
      debounceTimer.current = setTimeout(() => void fetchData(neu, 1, colState), 400)
    },
    [fetchData, colState],
  )

  const updateColState = useCallback((updater: (prev: ColumnState) => ColumnState) => {
    setColState((prev) => {
      const next = updater(prev)
      saveUiSetting(LAYOUT_KEY, next)
      return next
    })
  }, [])

  const handleSort = (key: string) => {
    if (!SERVER_SORTIERBAR.has(key)) return
    const dir: 'asc' | 'desc' = colState.sortKey === key && colState.sortDir === 'asc' ? 'desc' : 'asc'
    const next = { ...colState, sortKey: key, sortDir: dir }
    updateColState(() => next)
    setPage(1)
    void fetchData(filterValues, 1, next)
  }

  const handleDrop = (ziel: string) => {
    const quelle = dragKeyRef.current
    setDragOverKey(null)
    if (!quelle || quelle === ziel) return
    updateColState((prev) => {
      const keys = [...prev.columnKeys]
      const von = keys.indexOf(quelle)
      const nach = keys.indexOf(ziel)
      if (von < 0 || nach < 0) return prev
      keys.splice(von, 1)
      keys.splice(nach, 0, quelle)
      return { ...prev, columnKeys: keys }
    })
  }

  const handleResizeStart = (e: React.MouseEvent, key: string) => {
    e.preventDefault()
    const th = (e.target as HTMLElement).closest('th')
    const startW = th?.getBoundingClientRect().width ?? 120
    const startX = e.clientX
    const onMove = (ev: MouseEvent) => updateColState((prev) => ({ ...prev, columnWidths: { ...prev.columnWidths, [key]: Math.max(60, startW + ev.clientX - startX) } }))
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  const vermerken = async () => {
    if (!vermerk) return
    try {
      if (vermerk.art === 'resolve') await resolveAssemblyDefect(vermerk.zeile.id, notiz)
      else await reopenAssemblyDefect(vermerk.zeile.id, notiz || undefined)
      setVermerk(null)
      setNotiz('')
      void fetchData(filterValues, page, colState)
    } catch (e) {
      setError(fehlertext(e, t))
    }
  }

  const shownColumns = colState.columnKeys.map((k) => alleSpalten.find((c) => c.key === k)).filter((c): c is ColDef => !!c)
  const availableToAdd = alleSpalten.filter((c) => !colState.columnKeys.includes(c.key))
  const heute = new Date().toISOString().slice(0, 10)

  const renderCell = (z: MontageMangelZeile, col: ColDef): React.ReactNode => {
    switch (col.key) {
      case 'projectLabel':
        return z.projectLabel || '—'
      case 'documentNumber':
        return z.documentNumber ?? '—'
      case 'documentDate':
        return fmtDate(z.documentDate)
      case 'nr':
        return z.nr
      case 'partName':
        return z.partName ?? t('siteDocs.assembly.general')
      case 'text':
        return <span className="block max-w-md truncate" title={z.text}>{z.text}</span>
      case 'weight':
        return z.weight ? t(`siteDocs.assembly.weights.${z.weight}`) : ''
      case 'dueDate':
        return z.dueDate ? <span className={z.state === 'OPEN' && z.dueDate < heute ? 'font-medium text-destructive' : ''}>{fmtDate(z.dueDate)}</span> : ''
      case 'state':
        return (
          <span className="flex items-center gap-2">
            {t(`siteDocs.assembly.states.${z.state}`)}
            {z.resolvedByNumber ? ` (${z.resolvedByNumber})` : ''}
            {darfVermerken && z.state === 'OPEN' && z.documentStatus !== 'AWAITING' && (
              <button type="button" className="text-xs text-primary hover:underline" onClick={(e) => { e.stopPropagation(); setVermerk({ zeile: z, art: 'resolve' }) }}>
                {t('siteDocs.assembly.markResolved')}
              </button>
            )}
            {darfVermerken && z.state === 'RESOLVED_OFFICE' && (
              <button type="button" className="text-xs text-primary hover:underline" onClick={(e) => { e.stopPropagation(); setVermerk({ zeile: z, art: 'reopen' }) }}>
                {t('siteDocs.assembly.reopen')}
              </button>
            )}
          </span>
        )
      case 'resolvedNote':
        return z.resolvedNote ?? ''
      case 'task':
        return z.taskDoneAt ? t('siteDocs.assembly.taskDone') : z.projectTaskId ? t('siteDocs.defects.taskOpen') : ''
      default:
        return null
    }
  }

  const rows = result?.data ?? []
  const meta = result?.meta
  const totalCols = shownColumns.length + (columnConfigMode && availableToAdd.length ? 1 : 0) + 1
  const pfeil = (key: string) =>
    !SERVER_SORTIERBAR.has(key) ? null : colState.sortKey === key ? colState.sortDir === 'asc' ? <ArrowUp className="h-3 w-3 text-primary" /> : <ArrowDown className="h-3 w-3 text-primary" /> : <ArrowUpDown className="h-3 w-3 opacity-30" />

  return (
    <div className={pageWide}>
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">{t('siteDocs.defects.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{documentId ? t('siteDocs.defects.subtitleDocument') : t('siteDocs.defects.subtitle')}</p>
        </div>
        {darfExport && (
          <div className="flex items-center gap-2">
            <ExportButton
              defaultFilename="maengel"
              request={(format, orientation) => assemblyDefectsExportRequest({ ...filterParams(filterValues, documentId), sort: SERVER_SORTIERBAR.has(colState.sortKey) ? colState.sortKey : undefined, dir: colState.sortDir, format, orientation }, shownColumns.filter((c) => c.key !== 'task').map((c) => ({ key: c.key === 'weight' ? 'weightText' : c.key === 'state' ? 'stateText' : c.key, label: c.label })))}
              mail={{ source: 'EXPORT_SITE_ASSEMBLY_DEFECTS', title: t('mailExport.titles.EXPORT_SITE_ASSEMBLY_DEFECTS'), subject: t('siteDocs.defects.title') }}
            />
          </div>
        )}
      </div>

      <FilterBar availableFields={filterFields} value={filterValues} onChange={handleFiltersChange} layoutKey="site_docs_defects" defaultFieldKeys={DEFAULT_FILTER_FIELD_KEYS} adresse={adresse} />

      <div className="relative overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <button
          type="button"
          onClick={() => setColumnConfigMode((v) => !v)}
          className={`absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-md transition-colors ${columnConfigMode ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
          title={t('siteDocs.list.columns')}
        >
          {columnConfigMode ? <Check size={14} /> : <Settings size={14} />}
        </button>
        <HelpPopover topic="columns" className="absolute right-8 top-1.5" />

        {columnConfigMode && (
          <div className="border-b border-border bg-muted/30 px-3 py-2 pr-16">
            <LayoutDefaultActions
              settingKey={LAYOUT_KEY}
              currentValue={colState}
              onReset={(value) => {
                const next = (value as ColumnState | null) ?? STANDARD
                markUiSettingApplied(LAYOUT_KEY, next)
                setColState(next)
                void fetchData(filterValues, 1, next)
              }}
            />
          </div>
        )}

        {error && <div className="m-4 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                {shownColumns.map((col) => (
                  <th
                    key={col.key}
                    className={`relative select-none px-4 py-1.5 text-left font-medium text-foreground ${columnConfigMode ? 'cursor-grab' : ''} ${dragOverKey === col.key ? 'bg-primary/10 outline outline-2 outline-primary' : ''}`}
                    draggable={columnConfigMode}
                    onDragStart={columnConfigMode ? () => (dragKeyRef.current = col.key) : undefined}
                    onDragOver={columnConfigMode ? (e) => { e.preventDefault(); setDragOverKey(col.key) } : undefined}
                    onDrop={columnConfigMode ? () => handleDrop(col.key) : undefined}
                    onDragEnd={columnConfigMode ? () => setDragOverKey(null) : undefined}
                    style={colState.columnWidths[col.key] ? { width: colState.columnWidths[col.key] } : undefined}
                  >
                    <div className="flex items-center gap-1">
                      {col.sortable ? (
                        <button type="button" onClick={() => handleSort(col.key)} className="flex items-center gap-1 hover:text-primary">
                          {col.label} {pfeil(col.key)}
                        </button>
                      ) : (
                        <span>{col.label}</span>
                      )}
                      {columnConfigMode && (
                        <button type="button" onClick={() => updateColState((prev) => ({ ...prev, columnKeys: prev.columnKeys.filter((k) => k !== col.key) }))} className="ml-1 text-muted-foreground hover:text-destructive" title={t('siteDocs.list.removeColumn')}>
                          ✕
                        </button>
                      )}
                    </div>
                    <div className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40" onMouseDown={(e) => handleResizeStart(e, col.key)} />
                  </th>
                ))}
                {columnConfigMode && availableToAdd.length > 0 && (
                  <th className="px-2 py-1.5">
                    <select
                      className="rounded border border-border bg-background px-1 py-0.5 text-xs text-foreground"
                      defaultValue=""
                      onChange={(e) => {
                        const key = e.target.value
                        if (!key) return
                        updateColState((prev) => ({ ...prev, columnKeys: [...prev.columnKeys, key] }))
                        e.target.value = ''
                      }}
                    >
                      <option value="">{t('siteDocs.list.addColumn')}</option>
                      {availableToAdd.map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </th>
                )}
                <th className="w-4" />
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 && (
                <tr>
                  <td colSpan={totalCols} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    {t('common.loading')}
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={totalCols} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    {t('siteDocs.defects.empty')}
                  </td>
                </tr>
              )}
              {rows.map((z, idx) => (
                <tr key={z.id} onClick={() => navigate(`/site-docs/${z.documentId}`)} className={`cursor-pointer border-b border-border last:border-0 ${idx % 2 === 0 ? 'bg-background' : 'bg-muted/20'} transition-colors hover:bg-accent/30`}>
                  {shownColumns.map((col) => (
                    <td key={col.key} className="whitespace-nowrap px-4 py-1.5">
                      {renderCell(z, col)}
                    </td>
                  ))}
                  <td />
                </tr>
              ))}
              {rows.length > 0 && (
                <tr className="border-t-2 border-border bg-muted/50 font-medium">
                  <td colSpan={Math.max(1, shownColumns.length)} className="whitespace-nowrap px-4 py-1.5 text-muted-foreground">
                    {t('siteDocs.defects.count', { count: meta?.total ?? rows.length })}
                  </td>
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {meta && meta.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border px-4 py-2 text-sm text-muted-foreground">
            <span>{t('siteDocs.list.page', { page: meta.page, pages: meta.totalPages, total: meta.total })}</span>
            <div className="flex items-center gap-2">
              <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)} className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40">
                ‹
              </button>
              <button type="button" disabled={page >= meta.totalPages || loading} onClick={() => setPage((p) => p + 1)} className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40">
                ›
              </button>
            </div>
          </div>
        )}
      </div>

      {vermerk && (
        <Modal
          isOpen
          onClose={() => setVermerk(null)}
          title={vermerk.art === 'resolve' ? t('siteDocs.assembly.markResolved') : t('siteDocs.assembly.reopen')}
          size="sm"
          footer={
            <div className="flex w-full justify-end gap-2">
              <button type="button" className="h-10 rounded-md border px-4 text-sm" onClick={() => setVermerk(null)}>
                {t('common.cancel')}
              </button>
              <button type="button" className="h-10 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground" disabled={vermerk.art === 'resolve' && !notiz.trim()} onClick={() => void vermerken()}>
                {vermerk.art === 'resolve' ? t('siteDocs.assembly.markResolved') : t('siteDocs.assembly.reopen')}
              </button>
            </div>
          }
        >
          <p className="mb-2 text-sm">
            {vermerk.zeile.documentNumber} · {vermerk.zeile.nr}: {vermerk.zeile.text}
          </p>
          <p className="mb-2 text-sm text-muted-foreground">{vermerk.art === 'resolve' ? t('siteDocs.assembly.resolveHint') : t('siteDocs.assembly.reopenHint')}</p>
          <textarea className={`${feldKlasse} min-h-20`} value={notiz} onChange={(e) => setNotiz(e.target.value)} placeholder={t('siteDocs.assembly.note')} aria-label={t('siteDocs.assembly.note')} />
        </Modal>
      )}
    </div>
  )
}
